import React, { CSSProperties, useCallback, useEffect, useRef, useState } from 'react';
import { Card, Button, Typography, Row, Col, Modal, Collapse, Spin, message, Select } from 'antd';
import { useAppSelectionHeader } from 'context/AppSelectionHeaderContext';
import { CheckCircleFilled, CloseCircleFilled, CheckOutlined, HomeOutlined, UserAddOutlined, LockOutlined } from '@ant-design/icons';
import lsp from 'assets/images/cattle 2.png';
import crops from 'assets/images/crops 2.jpg';
import chemicalJournalImage from 'assets/images/chemical_journal.png';
import bulletinBoard from 'assets/images/bulletin-board.jpg';
import jbvPuff from 'assets/images/JBV-puff.jpeg';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from "react-i18next";
import { canInviteFarmUsers } from 'farmInviteAccess';
import { AccountInfoData, AssignedFarmUser, CropsCompany, GetAssignedFarmUsersData, LspModuleData, TFunction } from 'types';
import { Grid } from 'antd';
import { useCheckCropsPage } from './useCheckCrops';
import { useGetAssignedFarmUsers } from 'hooks/useFarms';
import { selfSyncFarmToCrops } from 'api/farms';
import {
  useAccountInfo,
  useSelfEnableBoardAccess,
  useSelfEnableCropsAccess,
  useSelfEnableLspAccess,
  useSetAccountFarm,
} from 'hooks/useAccount';
import { getLoggedInUsername, selfStartTrial } from 'api/account';
import { trialRemainingDays } from 'utils/trialRemainingDays';
import { useAllModules, useSelfAddModuleToAccount } from 'hooks/useModules';
import { useUserCompanies } from 'hooks/useCompanies';
import { cookies, storageKeys } from 'static';
import {
  FARM_CONTEXT_CHANGED_EVENT,
  FarmContext,
  getFarmContextFromAccountInfo,
  getStoredFarmContext,
  findFarmOptionById,
  hasRegisteredFarm,
  setCurrentFarmContext,
  userHasFarmAccess,
} from 'farmContext';
import { useFarmPickerCatalog } from 'hooks/useFarmPickerCatalog';
import { useBillingCompanies } from 'hooks/useBillingCompanies';
import { getFarmPickerContextMeta } from 'utils/farmPickerOptions';
import { findFarmInOrganisations, getBillingCompaniesForFarm } from 'utils/organisationContext';
const { useBreakpoint } = Grid;

const { Title } = Typography;
const FARM_ONBOARDING_CUTOFF_DATE = '2026-05-29';
const FARM_ID_FIELDS = ['id', 'farmId', 'farmCode'] as const;
const FARM_REGISTRATION_DATE_FIELDS = ['registeredDate', 'registrationDate', 'createdAt', 'createdDate'] as const;

const normalizeCropsBaseUrl = () => {
  const cropsBaseUrl = process.env.REACT_APP_CROPS_PAGE_URL;
  if (!cropsBaseUrl) {
    return undefined;
  }
  return cropsBaseUrl.endsWith('/') ? cropsBaseUrl : `${cropsBaseUrl}/`;
};

const getBulletinBoardUrl = () => {
  const normalizedBaseUrl = normalizeCropsBaseUrl();
  if (!normalizedBaseUrl) {
    return undefined;
  }
  return `${normalizedBaseUrl}#/home/BoardWidgets`;
};

const getChemicalJournalUrl = () => {
  const normalizedBaseUrl = normalizeCropsBaseUrl();
  if (!normalizedBaseUrl) {
    return undefined;
  }
  return `${normalizedBaseUrl}#/home/Chemicals`;
};

const getCardSpacing = (isMobile: boolean) => (isMobile ? 20 : 28);
const getPrimaryCtaButtonSize = (isMobile: boolean) => ({
  borderRadius: 12,
  fontSize: 16,
  fontWeight: 600,
  height: isMobile ? 48 : 54,
});
const ONBOARDING_PRIMARY_BUTTON_COLOR = '#4A4D4E';
const ONBOARDING_PRIMARY_BUTTON_SHADOW = '0 10px 20px rgba(52, 64, 84, 0.16)';
const CONFETTI_PLAY_DURATION_MS = 2500;
const CONFETTI_PARTICLE_COUNT = 95;

const CONFETTI_COLORS = ['#ff6b3d', '#12b76a', '#f79009', '#36bffa', '#ef5da8'];

interface ConfettiParticle {
  id: number,
  delay: number,
  duration: number,
  rotationStart: number,
  rotationMid: number,
  rotationEnd: number,
  width: number,
  height: number,
  color: string,
  borderRadius: number,
  startX: number,
  apexX: number,
  endX: number,
  apexY: number,
  endY: number,
}

const randomBetween = (min: number, max: number) => Math.random() * (max - min) + min;

const createRandomConfettiParticles = (count = CONFETTI_PARTICLE_COUNT): ConfettiParticle[] =>
  Array.from({ length: count }, (_, index) => {
    const isStreamer = Math.random() < 0.3;
    const rotationStart = randomBetween(0, 360);
    const spin = randomBetween(420, 1080) * (Math.random() < 0.5 ? -1 : 1);
    const spread = randomBetween(-1, 1);
    const burstPower = randomBetween(130, 450);
    const startX = randomBetween(-20, 20);
    const apexX = spread * burstPower + randomBetween(-45, 45);
    const endX = apexX + randomBetween(-220, 220);
    const apexY = -randomBetween(230, 520);
    const endY = randomBetween(520, 980);

    return {
      id: index,
      delay: randomBetween(0, 1200),
      duration: randomBetween(2800, 4700),
      rotationStart,
      rotationMid: rotationStart + spin * 0.55,
      rotationEnd: rotationStart + spin,
      width: isStreamer ? randomBetween(4, 8) : randomBetween(9, 19),
      height: isStreamer ? randomBetween(18, 34) : randomBetween(10, 20),
      color: CONFETTI_COLORS[Math.floor(Math.random() * CONFETTI_COLORS.length)],
      borderRadius: isStreamer ? randomBetween(1, 3) : randomBetween(2, 9),
      startX,
      apexX,
      endX,
      apexY,
      endY,
    };
  });

// Preload images so they're cached before render
[lsp, crops, bulletinBoard, jbvPuff].forEach((src) => {
  const link = document.createElement('link');
  link.rel = 'preload';
  link.as = 'image';
  link.href = src;
  document.head.appendChild(link);
});

const CROPS_COOKIE_DOMAIN = '.farmersplan.com';
const CROPS_COOKIE_DAYS = 7;

const setCookie = (name: string, value: string, days: number, domain?: string) => {
  const expires = new Date();
  expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);

  let cookie = `${name}=${value};expires=${expires.toUTCString()};path=/`;
  if (domain) {
    cookie += `;domain=${domain}`;
  }

  cookie += `;Secure;SameSite=Lax`;
  document.cookie = cookie;
};

const getCookie = (name: string) => {
  const encodedName = `${encodeURIComponent(name)}=`;
  const cookie = document.cookie
    .split(';')
    .map((part) => part.trim())
    .find((part) => part.startsWith(encodedName));

  if (!cookie) {
    return '';
  }

  return decodeURIComponent(cookie.slice(encodedName.length));
};

const getCropsRoleId = (company: CropsCompany, roleByAccountId: Record<number, number>) => {
  if (company.roleId && company.roleId > 0) {
    return company.roleId;
  }

  return roleByAccountId[company.id];
};

const setCropsSelectionCookies = (accountId: number, roleId?: number) => {
  setCookie(cookies.CropsSelectedAccount, String(accountId), CROPS_COOKIE_DAYS, CROPS_COOKIE_DOMAIN);
  if (roleId && roleId > 0) {
    setCookie(cookies.CropsRoleId, String(roleId), CROPS_COOKIE_DAYS, CROPS_COOKIE_DOMAIN);
  }
};

type AppCardItem = {
  name: string,
  imageSrc?: string | undefined,
  title: string,
  description?: string,
  features?: Array<{
    text: string,
    effect?: string,
    excluded?: boolean,
  }>,
  disabled: boolean,
  awaitingFarm?: boolean,
  isLoading?: boolean,
  url?: string,
  buttonLabel?: string,
  notice?: string,
  trialAction?: 'start',
  aboveCta?: React.ReactNode,
};

type SupportedAppName = 'livestock' | 'crops' | 'bulletinBoard';

type AccessOverrides = Partial<Record<SupportedAppName, boolean>>;

const isSupportedAppName = (value: string): value is SupportedAppName => {
  return value === 'livestock' || value === 'crops' || value === 'bulletinBoard';
};

const APP_MODULE_NAME_MATCHERS: Record<SupportedAppName, string[]> = {
  bulletinBoard: ['board', 'bulletin', 'gardstavla', 'gardstavlan'],
  crops: ['crops', 'vaxtodling'],
  livestock: ['lsp', 'livestock', 'slutgodning', 'diko'],
};

const normalizeModuleText = (value?: string) =>
  (value || '')
    .trim()
    .toLowerCase()
    .replace(/[åä]/g, 'a')
    .replace(/ö/g, 'o')
    .replace(/[\s_-]+/g, '');

const findModuleIdForApp = (modules: LspModuleData[], appName: SupportedAppName) => {
  const matchers = APP_MODULE_NAME_MATCHERS[appName];

  const foundModule = modules.find((moduleData) => {
    const moduleName = normalizeModuleText(moduleData?.name);
    const moduleDescription = normalizeModuleText(moduleData?.description);

    return matchers.some((matcher) => {
      const normalizedMatcher = normalizeModuleText(matcher);
      return moduleName.includes(normalizedMatcher) || moduleDescription.includes(normalizedMatcher);
    });
  });

  return foundModule?.id;
};

const getFarmOnboardingHiddenStorageKey = (farmId: string) => `${storageKeys.HideFarmOnboardingPrefix}${farmId}`;
const hiddenEmailFragments = ['hencol', 'farmersplan', 'gislen'];

const getStringValue = (value: string | number | null | undefined) => {
  if (value === undefined || value === null) {
    return '';
  }

  return String(value).trim();
};

const getAccessibleFarmNames = (accountInfo?: AccountInfoData) => {
  const farmNames = [
    ...(accountInfo?.userFarms ?? []).map((farm) => getStringValue(farm?.farmName)),
    ...(accountInfo?.userFarmRoles ?? []).map((farmRole) => getStringValue(farmRole?.farmName)),
    getStringValue(accountInfo?.selectedFarmName),
  ].filter(Boolean);

  const uniqueByLowercase = new Set<string>();
  return farmNames.filter((name) => {
    const normalized = name.toLowerCase();
    if (uniqueByLowercase.has(normalized)) {
      return false;
    }

    uniqueByLowercase.add(normalized);
    return true;
  });
};

const shouldShowFarmOnboarding = (accountInfo?: AccountInfoData) =>
  !hasRegisteredFarm(accountInfo);

const parseDateValue = (value?: string | number | null) => {
  const normalizedValue = getStringValue(value);

  if (!normalizedValue) {
    return null;
  }

  const date = new Date(normalizedValue);

  return Number.isNaN(date.getTime()) ? null : date;
};

const isAfterFarmOnboardingCutoff = (value?: string | number | null) => {
  const date = parseDateValue(value);

  if (!date) {
    return false;
  }

  const cutoffDate = new Date(`${FARM_ONBOARDING_CUTOFF_DATE}T00:00:00`);

  return date.getTime() > cutoffDate.getTime();
};

const farmMatchesId = (farm: NonNullable<AccountInfoData['userFarms']>[number], farmId: string) =>
  FARM_ID_FIELDS.some((field) => getStringValue(farm?.[field]) === farmId);

const getSessionFarmRegistrationDate = (farmId: string) => {
  const registeredFarmId = getStringValue(sessionStorage.getItem(storageKeys.FarmOnboardingRegistrationFarmId));

  if (registeredFarmId && registeredFarmId !== farmId) {
    return '';
  }

  return getStringValue(sessionStorage.getItem(storageKeys.FarmOnboardingRegistrationDate));
};

const isFarmRegisteredAfterOnboardingCutoff = (accountInfo: AccountInfoData | undefined, farmId: string) => {
  const sessionRegistrationDate = getSessionFarmRegistrationDate(farmId);

  if (isAfterFarmOnboardingCutoff(sessionRegistrationDate)) {
    return true;
  }

  const matchingFarms = (accountInfo?.userFarms ?? []).filter((farm) => farmMatchesId(farm, farmId));

  return matchingFarms.some((farm) =>
    FARM_REGISTRATION_DATE_FIELDS.some((field) => isAfterFarmOnboardingCutoff(farm?.[field]))
  );
};

const shouldForceConfettiFromSearch = (search: string) => {
  const params = new URLSearchParams(search);

  if (!params.has('confetti')) {
    return false;
  }

  const value = params.get('confetti');

  if (!value) {
    return true;
  }

  const normalizedValue = value.trim().toLowerCase();
  return normalizedValue === '1' || normalizedValue === 'true' || normalizedValue === 'yes' || normalizedValue === 'on';
};

const extractAssignedFarmUsers = (data?: GetAssignedFarmUsersData | null): AssignedFarmUser[] => {
  if (Array.isArray(data)) {
    return data;
  }

  const users = data?.users;
  if (Array.isArray(users)) {
    return users;
  }

  return [];
};

const shouldHideEmail = (email: string) => {
  const normalized = getStringValue(email).toLowerCase();

  if (!normalized) {
    return false;
  }

  return hiddenEmailFragments.some((fragment) => normalized.includes(fragment));
};

interface FarmOnboardingProps {
  createFarmCompletionAnimationToken: number,
  holdCreateFarmCompletion: boolean,
  isCreateFarmCompleted: boolean,
  isInviteUsersCompleted: boolean,
  isMobile: boolean,
  onHideFarmOnboarding: () => void,
  onCreateFarm: () => void,
  onInviteUsers: () => void,
  shouldShowHideFarmOnboardingButton: boolean,
  showInviteUsersStep: boolean,
}

const FarmOnboarding: React.FC<FarmOnboardingProps> = ({
  createFarmCompletionAnimationToken,
  holdCreateFarmCompletion,
  isCreateFarmCompleted,
  isInviteUsersCompleted,
  isMobile,
  onHideFarmOnboarding,
  onCreateFarm,
  onInviteUsers,
  shouldShowHideFarmOnboardingButton,
  showInviteUsersStep,
}) => {
  const { t }: TFunction = useTranslation();
  const primaryCtaButtonSize = getPrimaryCtaButtonSize(isMobile);
  const [createFarmCompletionStage, setCreateFarmCompletionStage] = useState<'idle' | 'check' | 'dim'>('dim');

  useEffect(() => {
    if (holdCreateFarmCompletion) {
      setCreateFarmCompletionStage('idle');
      return;
    }

    if (!isCreateFarmCompleted) {
      setCreateFarmCompletionStage('idle');
      return;
    }

    if (createFarmCompletionAnimationToken === 0) {
      setCreateFarmCompletionStage('dim');
      return;
    }

    setCreateFarmCompletionStage('idle');
    const checkTimer = window.setTimeout(() => {
      setCreateFarmCompletionStage('check');
    }, 250);
    const dimTimer = window.setTimeout(() => {
      setCreateFarmCompletionStage('dim');
    }, 1000);

    return () => {
      window.clearTimeout(checkTimer);
      window.clearTimeout(dimTimer);
    };
  }, [createFarmCompletionAnimationToken, holdCreateFarmCompletion, isCreateFarmCompleted]);

  const createFarmStepIsCompleted = isCreateFarmCompleted && !holdCreateFarmCompletion && createFarmCompletionStage !== 'idle';
  const createFarmStepIsDimmed = isCreateFarmCompleted && !holdCreateFarmCompletion && createFarmCompletionStage === 'dim';
  const createFarmStepIsAnimating = isCreateFarmCompleted && !holdCreateFarmCompletion && createFarmCompletionStage === 'check';
  const shouldDelayInviteUsersStepUnlock = createFarmCompletionAnimationToken > 0 && createFarmCompletionStage !== 'dim';
  const isInviteUsersStepLocked = !isCreateFarmCompleted || holdCreateFarmCompletion || shouldDelayInviteUsersStepUnlock;

  const renderStep = ({
    buttonLabel,
    icon,
    isDisabled = false,
    isButtonDisabled = isDisabled,
    isCompleted,
    isCompletionAnimating = false,
    isDimmed = false,
    isPrimary = false,
    number,
    onClick,
    secondaryAction,
    stepLabel,
    text,
    title,
  }: {
    buttonLabel: string,
    icon: React.ReactNode,
    isButtonDisabled?: boolean,
    isDisabled?: boolean,
    isCompleted: boolean,
    isCompletionAnimating?: boolean,
    isDimmed?: boolean,
    isPrimary?: boolean,
    number: number,
    onClick: () => void,
    secondaryAction?: {
      label: string,
      onClick: () => void,
      isDisabled?: boolean,
    },
    stepLabel: string,
    text: string,
    title: string,
  }) => (
    <div
      style={{
        width: '100%',
        height: '100%',
        display: 'grid',
        gridTemplateColumns: isMobile ? 'auto minmax(0, 1fr)' : 'auto minmax(0, 1fr) auto',
        gap: isMobile ? '12px 12px' : '12px 14px',
        alignItems: 'center',
        padding: isMobile ? 16 : '16px 20px',
        borderRadius: 16,
        background: isDisabled ? '#f7f8fa' : '#ffffff',
        border: isDisabled ? '1px dashed rgba(16, 24, 40, 0.25)' : '1px solid rgba(255, 107, 61, 0.14)',
        boxShadow: isDisabled ? 'none' : '0 16px 34px rgba(29, 33, 41, 0.09)',
        textAlign: 'left',
        opacity: isDisabled ? 0.85 : isDimmed ? 0.68 : 1,
        transition: 'opacity 360ms ease, box-shadow 360ms ease, border-color 360ms ease',
      }}
    >
      <div
        aria-hidden="true"
        style={{
          width: isMobile ? 38 : 44,
          height: isMobile ? 38 : 44,
          borderRadius: isCompleted ? '50%' : 12,
          background: isCompleted ? '#12b76a' : isDisabled ? '#98a2b3' : '#ff6b3d',
          color: '#ffffff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: isMobile ? 17 : 19,
          fontWeight: 800,
          boxShadow: isCompleted
            ? '0 12px 24px rgba(18, 183, 106, 0.22)'
            : isDisabled
              ? 'none'
              : '0 12px 24px rgba(255, 107, 61, 0.24)',
          transition: 'background-color 320ms ease, border-radius 320ms ease, box-shadow 320ms ease, transform 320ms ease',
          transform: isCompletionAnimating ? 'scale(1.12)' : 'scale(1)',
        }}
      >
        {isCompleted ? <CheckOutlined style={{ fontSize: isMobile ? 17 : 19 }} /> : number}
      </div>
      <div style={{ minWidth: 0 }}>
        <Typography.Text
          style={{
            color: isDisabled ? '#667085' : '#8c4e33',
            display: 'block',
            fontSize: 13,
            fontWeight: 700,
            marginBottom: 2,
          }}
        >
          {t('AppSelectionOnboardingEyebrow')} - {stepLabel}
        </Typography.Text>
        <Title
          level={3}
          style={{
            margin: 0,
            color: isDisabled ? '#475467' : '#101828',
            fontSize: isMobile ? 19 : 22,
            lineHeight: 1.2,
          }}
        >
          {title}
        </Title>
        <Typography.Paragraph
          style={{
            margin: '5px 0 0',
            color: isDisabled ? '#667085' : '#5d6670',
            fontSize: isMobile ? 14 : 16,
            lineHeight: 1.45,
          }}
        >
          {text}
        </Typography.Paragraph>
        {isDisabled ? (
          <Typography.Text
            style={{
              marginTop: 8,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              color: '#475467',
              fontSize: 13,
              fontWeight: 600,
            }}
          >
            <LockOutlined />
            {t('AppSelectionOnboardingStepLockedInfo')}
          </Typography.Text>
        ) : null}
      </div>
      <div
        style={{
          gridColumn: isMobile ? '1 / -1' : '3 / 4',
          gridRow: isMobile ? undefined : '1 / 3',
          justifySelf: 'end',
          alignSelf: isMobile ? 'start' : 'center',
          display: 'flex',
          gap: 8,
          flexWrap: 'wrap',
          justifyContent: 'flex-end',
        }}
      >
        {secondaryAction ? (
          <Button
            type="default"
            size="large"
            onClick={secondaryAction.onClick}
            disabled={secondaryAction.isDisabled}
            style={{
              minWidth: isMobile ? 120 : 130,
              ...primaryCtaButtonSize,
              borderColor: '#d0d5dd',
              color: secondaryAction.isDisabled ? '#98a2b3' : '#667085',
              backgroundColor: secondaryAction.isDisabled ? '#f2f4f7' : '#ffffff',
              paddingInline: 16,
              whiteSpace: 'nowrap',
              transition: 'background-color 240ms ease, color 240ms ease, border-color 240ms ease',
            }}
          >
            {secondaryAction.label}
          </Button>
        ) : null}
        <Button
          className="farm-onboarding-action-button"
          type={isPrimary ? 'primary' : 'default'}
          size="large"
          icon={icon}
          onClick={onClick}
          disabled={isButtonDisabled}
          style={{
            width: 'auto',
            minWidth: isMobile ? 150 : 160,
            ...primaryCtaButtonSize,
            backgroundColor: isButtonDisabled ? '#f2f4f7' : isPrimary ? ONBOARDING_PRIMARY_BUTTON_COLOR : '#ffffff',
            borderColor: isButtonDisabled ? '#d0d5dd' : isPrimary ? ONBOARDING_PRIMARY_BUTTON_COLOR : '#ff6b3d',
            color: isButtonDisabled ? '#98a2b3' : isPrimary ? '#ffffff' : '#8c4e33',
            paddingInline: 16,
            whiteSpace: 'nowrap',
            boxShadow: isButtonDisabled
              ? 'none'
              : isPrimary
                ? ONBOARDING_PRIMARY_BUTTON_SHADOW
                : undefined,
            transition: 'background-color 240ms ease, color 240ms ease, border-color 240ms ease, box-shadow 240ms ease',
          }}
        >
          {buttonLabel}
        </Button>
      </div>
    </div>
  );

  return (
    <div
      style={{
        width: '100%',
        display: 'grid',
        gridTemplateColumns: '1fr',
        gap: isMobile ? 12 : 14,
        alignItems: 'stretch',
      }}
    >
      {!isCreateFarmCompleted ? renderStep({
        buttonLabel: t('AppSelectionOnboardingCreateFarmButton'),
        icon: <HomeOutlined />,
        isButtonDisabled: isCreateFarmCompleted,
        isCompleted: createFarmStepIsCompleted,
        isCompletionAnimating: createFarmStepIsAnimating,
        isDimmed: createFarmStepIsDimmed,
        isPrimary: true,
        number: 1,
        onClick: onCreateFarm,
        stepLabel: t('AppSelectionOnboardingStepLabel'),
        text: t('AppSelectionOnboardingStepCreateFarmText'),
        title: t('AppSelectionOnboardingStepCreateFarm'),
      }) : null}
      {showInviteUsersStep ? renderStep({
        buttonLabel: t('AppSelectionOnboardingInviteUsersButton'),
        icon: isInviteUsersStepLocked ? <LockOutlined /> : <UserAddOutlined />,
        isDisabled: isInviteUsersStepLocked,
        isCompleted: isInviteUsersCompleted,
        isPrimary: true,
        number: 2,
        onClick: onInviteUsers,
        secondaryAction: shouldShowHideFarmOnboardingButton ? {
          label: t('AppSelectionOnboardingHide'),
          onClick: onHideFarmOnboarding,
          // Hide must always work — do not tie it to invite-step lock state.
          isDisabled: false,
        } : undefined,
        stepLabel: t('AppSelectionOnboardingStepInviteUsersLabel'),
        text: t('AppSelectionOnboardingStepInviteUsersText'),
        title: t('AppSelectionOnboardingStepInviteUsers'),
      }) : null}
    </div>
  );
};

const ConfettiBurst = ({ isActive }: { isActive: boolean }) => {
  const [particles, setParticles] = useState<ConfettiParticle[]>(() => createRandomConfettiParticles());

  useEffect(() => {
    if (!isActive) {
      return;
    }

    setParticles(createRandomConfettiParticles());
  }, [isActive]);

  if (!isActive) {
    return null;
  }

  return (
    <div className="farm-onboarding-confetti" aria-hidden="true">
      {particles.map((particle) => {
        const particleStyle: CSSProperties & Record<string, string | number> = {
          left: '50%',
          top: '54vh',
          animationDelay: `${particle.delay}ms`,
          animationDuration: `${particle.duration}ms`,
          backgroundColor: particle.color,
          width: particle.width,
          height: particle.height,
          borderRadius: particle.borderRadius,
          '--confetti-start-x': `${particle.startX}px`,
          '--confetti-apex-x': `${particle.apexX}px`,
          '--confetti-end-x': `${particle.endX}px`,
          '--confetti-apex-y': `${particle.apexY}px`,
          '--confetti-end-y': `${particle.endY}px`,
          '--confetti-rotation-start': `${particle.rotationStart}deg`,
          '--confetti-rotation-mid': `${particle.rotationMid}deg`,
          '--confetti-rotation-end': `${particle.rotationEnd}deg`,
        };

        return (
          <span
            key={particle.id}
            className="farm-onboarding-confetti-piece"
            style={particleStyle}
          />
        );
      })}
    </div>
  );
};

interface AppCardProps {
  item: AppCardItem,
  onGo: (item: AppCardItem) => void;
  reserveCompanySlot?: boolean;
  disabled?: boolean;
  // isRemembered: boolean;
  // onRememberChange: (title: string, checked: boolean) => void;
}

const AppCard: React.FC<AppCardProps> = ({  
  item, 
  onGo,
  reserveCompanySlot = false,
  // isRemembered,
  // onRememberChange
}) => {
  
  const { t }: TFunction = useTranslation();
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  const isWide = screens.xl;
  const imageHeight = 300;
  const cardPadding = isMobile ? 16 : isWide ? 16 : getCardSpacing(isMobile);
  const primaryCtaButtonSize = getPrimaryCtaButtonSize(isMobile);
  const titleSize = isMobile ? 19 : 22;
  const titleBlockHeight = Math.ceil(titleSize * 1.25 * 2);
  const ctaLabel = item.buttonLabel || t('Open');
  const isOpenDisabled = item.disabled || item.isLoading;
  const isTrialCta = item.trialAction === 'start';
  const ctaColor = isTrialCta ? '#4a4d4e' : '#ff6b3d';
  const ctaShadowColor = isTrialCta ? 'rgba(74, 77, 78, 0.3)' : 'rgba(255, 107, 61, 0.3)';
  // Keep original type sizes; only remove empty mid-card stretch.
  const descriptionBlockHeight = isMobile ? 66 : 72;
  const companySlotHeight = 52;
  const featureList = item.features?.length ? (
    <div
      style={{
        textAlign: 'left',
        marginTop: isMobile ? 12 : 16,
        display: 'grid',
        gap: isWide ? 8 : 12,
        width: '100%',
        boxSizing: 'border-box',
        paddingLeft: 30,
        paddingRight: 30,
        paddingBottom: 8,
      }}
    >
      {item.features.map((feature) => (
        <div
          key={feature.text}
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 10,
            padding: '0 0 4px',
          }}
        >
          <div
            style={{
              width: isMobile ? 20 : 22,
              minWidth: isMobile ? 20 : 22,
              height: isMobile ? 20 : 22,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              marginTop: -2,
            }}
          >
            {feature.excluded ? (
              <CloseCircleFilled style={{ fontSize: isMobile ? 18 : 20, color: '#bfbfbf' }} />
            ) : (
            <CheckCircleFilled style={{ fontSize: isMobile ? 18 : 20, color: '#52c41a' }} />
            )}
          </div>
          <div style={{ minWidth: 0 }}>
            <div
              style={{
                color: '#2a2f36',
                fontSize: isMobile ? 13 : 14,
                fontWeight: 600,
                lineHeight: 1.25,
              }}
            >
              {feature.text}
            </div>
          </div>
        </div>
      ))}
    </div>
  ) : null;

  return (
    <Card 
      hoverable={!isOpenDisabled}
      style={{ 
        textAlign: 'center', 
        width: '100%',
        height: '100%',
        flex: '1 1 auto',
        cursor: isOpenDisabled ? 'default' : 'pointer',
        opacity: item.awaitingFarm ? 0.3 : 1,
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 20,
        overflow: 'hidden',
        background: 'rgba(255, 255, 255, 0.95)',
        boxShadow: '0 24px 60px rgba(23, 25, 28, 0.12)',
      }}
      bodyStyle={{
        padding: cardPadding,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-start',
        height: '100%',
      }}
      onClick={isOpenDisabled ? undefined : () => onGo(item)}
    >
      {item.imageSrc ? (
          <div style={{ 
          marginBottom: 16,
            width: '100%',
            height: imageHeight,
            overflow: 'hidden',
            borderRadius: 16,
            backgroundImage: `url('${item.imageSrc}')`,
            backgroundPosition: item.name === 'livestock' ? 'center 20%' : 'center',
            backgroundSize: 'cover',
            backgroundRepeat: 'no-repeat',
          boxShadow: 'inset 0 0 0 1px rgba(255, 255, 255, 0.55)',
          flexShrink: 0,
        }}
        />
      ) : null}

        <Title level={3} style={{ 
        margin: '0 0 6px',
        height: titleBlockHeight,
          fontSize: titleSize,
          fontWeight: 600,
          letterSpacing: '0.2px',
        lineHeight: 1.25,
        overflow: 'hidden',
        flexShrink: 0,
        }}>
          {item.title}
        </Title>

          <Typography.Paragraph
            style={{
          margin: '0 0 12px',
          height: descriptionBlockHeight,
              color: '#5d6670',
              fontSize: isMobile ? 14 : 15,
              lineHeight: 1.55,
          overflow: 'hidden',
          flexShrink: 0,
            }}
          >
        {item.description || ''}
          </Typography.Paragraph>

      {reserveCompanySlot ? (
        <div
          style={{ height: companySlotHeight, marginBottom: 12, overflow: 'hidden', flexShrink: 0 }}
            onClick={(event) => event.stopPropagation()}
            onMouseDown={(event) => event.stopPropagation()}
          >
            {item.aboveCta}
          </div>
        ) : null}

        <Button 
          type="primary" 
          size="large"
          loading={item.isLoading}
          disabled={isOpenDisabled}
        onClick={isOpenDisabled ? undefined : (event) => {
          event.stopPropagation();
          onGo(item);
        }}
          style={{ 
            width: '100%',
          backgroundColor: isOpenDisabled ? undefined : ctaColor,
          borderColor: isOpenDisabled ? undefined : ctaColor,
            ...primaryCtaButtonSize,
          boxShadow: isOpenDisabled ? undefined : `0 14px 30px ${ctaShadowColor}`,
          flexShrink: 0,
          }}
        >
          {ctaLabel}
        </Button>

      {item.notice ? (
        <div style={{
          marginTop: 10,
          color: '#9a3412',
          fontSize: 13,
          fontWeight: 600,
          lineHeight: 1.4,
          textAlign: 'center',
          flexShrink: 0,
        }}>
          {item.notice}
        </div>
      ) : null}

        {featureList ? (
          isMobile ? (
            <div
              style={{
              marginTop: 12,
                borderRadius: 12,
                background: '#ffffff',
                border: '1px solid #d9d9d9',
                overflow: 'hidden',
              flexShrink: 0,
              }}
              onClick={(event) => event.stopPropagation()}
            >
              <Collapse
                ghost
                size="small"
                expandIconPosition="end"
                items={[
                  {
                    key: `${item.name}-features`,
                    label: (
                      <span style={{ color: '#000000', fontSize: 14, fontWeight: 600 }}>
                        {t('showFeatures')}
                      </span>
                    ),
                    children: featureList,
                  },
                ]}
              />
            </div>
          ) : (
          <div style={{ flexShrink: 0 }}>{featureList}</div>
          )
        ) : null}
    </Card>
  );
};

const ChooseApplicationPage = () => {

  const { t, i18n }: TFunction & { i18n: { language?: string } } = useTranslation();
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  const cardSpacing = getCardSpacing(isMobile);
  const horizontalCardGutter = isMobile ? 0 : cardSpacing;
  const pageSidePadding = isMobile ? 16 : 24;
  const cardsContentWidth = '100%';
  const cardsContentMaxWidth = 1440;
  const cardsGridWidth = isMobile ? '100%' : `calc(100% - ${horizontalCardGutter}px)`;
  const cardsGridMaxWidth = isMobile ? 1440 : 1440 - horizontalCardGutter;
  const cardsGridOffset = isMobile ? 0 : horizontalCardGutter / 2;
  const location = useLocation();
  const navigate = useNavigate();
  const completeRegistration = location.state?.completeRegistration;
  const farmIdFromState = location.state?.newFarmId;
  const {
    data: accountInfo,
    refetch: refetchAccountInfo,
    isLoading: isLoadingAccountInfo,
    isError: failedLoadingAccountInfo,
  } = useAccountInfo();
  const { checkCrops } = useCheckCropsPage();
  const { mutateAsync: selfEnableLspAccess, isLoading: isSelfEnablingLspAccess } = useSelfEnableLspAccess();
  const { mutateAsync: selfEnableBoardAccess, isLoading: isSelfEnablingBoardAccess } = useSelfEnableBoardAccess();
  const { mutateAsync: selfEnableCropsAccess, isLoading: isSelfEnablingCropsAccess } = useSelfEnableCropsAccess();
  const { mutateAsync: getAllModules, isLoading: isLoadingAllModules } = useAllModules();
  const { mutateAsync: selfAddModuleToAccount, isLoading: isSelfAddingModuleToAccount } = useSelfAddModuleToAccount();
  const { mutate: loadAssignedFarmUsers } = useGetAssignedFarmUsers();
  const { mutateAsync: setAccountFarm, isLoading: isSettingAccountFarm } = useSetAccountFarm();
  const { data: cropsCompanies = [], isLoading: isLoadingCropsCompanies, isFetching: isFetchingCropsCompanies, refetch: refetchCropsCompanies } = useUserCompanies();
  const trialBillingPickRef = useRef<number | null>(null);
  const { setFarmPicker } = useAppSelectionHeader();

  const [showNoCropsModal, setShowNoCropsModal] = useState(false);
  const [showWelcomeModal, setShowWelcomeModal] = useState<boolean>(Boolean(location.state?.showWelcome));
  const [enablingAppName, setEnablingAppName] = useState<SupportedAppName | null>(null);
  const [accessOverrides, setAccessOverrides] = useState<AccessOverrides>({});
  const [moduleIdOverrides, setModuleIdOverrides] = useState<Partial<Record<SupportedAppName, string | number>>>({});
  const [assignedFarmUsers, setAssignedFarmUsers] = useState<AssignedFarmUser[] | null>(null);
  const [playFarmOnboardingCelebration, setPlayFarmOnboardingCelebration] = useState(false);
  const [createFarmCompletionAnimationToken, setCreateFarmCompletionAnimationToken] = useState(0);
  const [holdCreateFarmCompletion, setHoldCreateFarmCompletion] = useState(false);
  const [isFarmOnboardingHidden, setIsFarmOnboardingHidden] = useState(false);
  const [linkingCropsFarm, setLinkingCropsFarm] = useState(false);
  const [selectedFarmContext, setSelectedFarmContext] = useState<FarmContext>(() => getStoredFarmContext());
  const [selectedCropsCompanyId, setSelectedCropsCompanyId] = useState<number | undefined>();
  const [cropsRoleByAccountId, setCropsRoleByAccountId] = useState<Record<number, number>>({});
  const celebrationTimerRef = useRef<number | null>(null);
  const cropsSyncAttemptedRef = useRef('');
  const setFarmAttemptedRef = useRef('');
  const isCelebrationRunningRef = useRef(false);
  const isConfettiDebugEnabled = shouldForceConfettiFromSearch(location.search);
  const {
    farmOptions: livestockFarmOptions,
    pickerOptions: livestockPickerOptions,
    organisations,
    isLoadingOrganisations,
  } = useFarmPickerCatalog(accountInfo);
  const currentFarmContext = getFarmContextFromAccountInfo(accountInfo, selectedFarmContext.farmId);
  const activeFarmId = String(farmIdFromState || getStringValue(currentFarmContext.farmId) || '');
  const {
    data: billingCompanies = [],
    isLoading: isLoadingBillingCompanies,
    isFetching: isFetchingBillingCompanies,
    refetch: refetchBillingCompanies,
  } = useBillingCompanies(activeFarmId || undefined);
  const farmIdForOnboarding = activeFarmId;
  const hasKnownFarmAccess = !isLoadingAccountInfo && !failedLoadingAccountInfo;
  const hasFarmCatalogEntry = livestockFarmOptions.length > 0;
  const canUseActiveFarm =
    Boolean(activeFarmId) &&
    (Boolean(findFarmOptionById(livestockFarmOptions, activeFarmId)) ||
      userHasFarmAccess(accountInfo, activeFarmId) ||
      findFarmInOrganisations(organisations, activeFarmId));
  const requiresFarmOnboarding =
    hasKnownFarmAccess &&
    shouldShowFarmOnboarding(accountInfo) &&
    !hasFarmCatalogEntry &&
    !canUseActiveFarm;
  const appsLockedUntilFarm = !hasKnownFarmAccess || requiresFarmOnboarding;
  const accountEmailNormalized = getStringValue(accountInfo?.email).toLowerCase();
  // Keep the signed-in user in the count (gislen/hencol emails were incorrectly filtered out).
  const visibleAssignedFarmUsers = (assignedFarmUsers ?? []).filter((user) => {
    const email = getStringValue(user.email).toLowerCase();
    if (!email) {
      return false;
    }
    if (accountEmailNormalized && email === accountEmailNormalized) {
      return true;
    }
    return !shouldHideEmail(email);
  });
  const isCreateFarmCompleted = !isLoadingAccountInfo && !failedLoadingAccountInfo && !requiresFarmOnboarding;
  const isInviteUsersCompleted = isCreateFarmCompleted && visibleAssignedFarmUsers.length > 1;
  const showPostCreateFarmOnboarding =
    isCreateFarmCompleted &&
    !isInviteUsersCompleted &&
    Boolean(farmIdForOnboarding) &&
    isFarmRegisteredAfterOnboardingCutoff(accountInfo, farmIdForOnboarding);
  const showFarmOnboarding = requiresFarmOnboarding || showPostCreateFarmOnboarding;
  const shouldShowHideFarmOnboardingButton = Boolean(farmIdForOnboarding) && isCreateFarmCompleted && !isInviteUsersCompleted;
  // Once hidden for this farm, stay hidden (confetti debug must not force the banner back).
  const shouldRenderFarmOnboarding =
    !isFarmOnboardingHidden &&
    (showFarmOnboarding || failedLoadingAccountInfo);
  const selectedCropsCompany = cropsCompanies.find((company) => company.id === selectedCropsCompanyId);
  const isLoadingCropsAccounts = isLoadingCropsCompanies || (isFetchingCropsCompanies && !cropsCompanies.length);
  const cropsCompanyForActiveFarm = cropsCompanies.find((company) => String(company.extCompanyId) === String(activeFarmId));
  const cropsCardsNeedFarmLink = !appsLockedUntilFarm && !cropsCompanies.length && Boolean(activeFarmId) && !cropsCompanyForActiveFarm;
  const cropsAppsLocked = appsLockedUntilFarm || (!cropsCompanies.length && (linkingCropsFarm || cropsCardsNeedFarmLink));
  const accountModuleRows = accountInfo?.allowedModules || [];
  const hasFreeChemicalJournalAccess =
    (hasRegisteredFarm(accountInfo) || hasFarmCatalogEntry || canUseActiveFarm || Boolean(activeFarmId)) &&
    accountModuleRows.some((moduleRow) => {
      const name = String(moduleRow?.name || '').toLowerCase();
      if (name !== 'kemi' || moduleRow?.isActive === false) {
        return false;
      }
      const plan = String(moduleRow?.plan || '').toLowerCase();
      return !plan || plan === 'free';
    });
  const chemicalJournalLocked = appsLockedUntilFarm || (!hasFreeChemicalJournalAccess && cropsAppsLocked);
  const cropsTrialBlocked = appsLockedUntilFarm || linkingCropsFarm;
  const selectedLivestockFarm =
    findFarmOptionById(livestockFarmOptions, activeFarmId) ||
    findFarmOptionById(livestockFarmOptions, accountInfo?.selectedFarmId) ||
    (activeFarmId
      ? {
          farmId: activeFarmId,
          farmIdCandidates: [activeFarmId],
          farmName: getStringValue(selectedFarmContext.farmName) || activeFarmId,
          label: getStringValue(selectedFarmContext.farmName) || activeFarmId,
          orgNumber: getStringValue(selectedFarmContext.orgNumber) || undefined,
          value: activeFarmId,
        }
      : undefined);

  useEffect(() => {
    if (!activeFarmId || isLoadingAccountInfo || isSettingAccountFarm || failedLoadingAccountInfo) {
      return;
    }

    const selectedId = getStringValue(accountInfo?.selectedFarmId);
    if (selectedId === activeFarmId) {
      setFarmAttemptedRef.current = '';
      return;
    }

    // Avoid retry storms when set-farm fails (invited users previously looped here).
    if (setFarmAttemptedRef.current === activeFarmId) {
      return;
    }

    const canSelect =
      Boolean(findFarmOptionById(livestockFarmOptions, activeFarmId)) ||
      userHasFarmAccess(accountInfo, activeFarmId) ||
      findFarmInOrganisations(organisations, activeFarmId);

    if (!canSelect) {
      return;
    }

    setFarmAttemptedRef.current = activeFarmId;
    void setAccountFarm(activeFarmId)
      .then(() => {
        void refetchAccountInfo();
        void refetchCropsCompanies();
      })
      .catch(() => {
        message.error(t('AnErrorOccurred'));
      });
  }, [
    accountInfo?.selectedFarmId,
    activeFarmId,
    failedLoadingAccountInfo,
    isLoadingAccountInfo,
    isSettingAccountFarm,
    livestockFarmOptions,
    organisations,
    refetchAccountInfo,
    refetchCropsCompanies,
    setAccountFarm,
    t,
  ]);

  useEffect(() => {
    checkCrops();
  }, [checkCrops]);

  useEffect(() => {
    if (farmIdFromState) {
      void refetchAccountInfo();
      void refetchCropsCompanies();
    }
  }, [farmIdFromState, refetchAccountInfo, refetchCropsCompanies]);

  useEffect(() => {
    if (!hasKnownFarmAccess || isLoadingCropsCompanies || isFetchingCropsCompanies || !cropsCardsNeedFarmLink || !activeFarmId) {
      return;
    }
    if (cropsSyncAttemptedRef.current === activeFarmId) {
      return;
    }

    cropsSyncAttemptedRef.current = activeFarmId;
    setLinkingCropsFarm(true);
    selfSyncFarmToCrops(activeFarmId)
      .then(() => {
        void refetchCropsCompanies();
        void refetchAccountInfo();
      })
      .catch(() => {
        cropsSyncAttemptedRef.current = '';
        message.error(t('AnErrorOccurred'));
      })
      .finally(() => {
        setLinkingCropsFarm(false);
      });
  }, [activeFarmId, cropsCardsNeedFarmLink, hasKnownFarmAccess, isFetchingCropsCompanies, isLoadingCropsCompanies, refetchAccountInfo, refetchCropsCompanies, t]);

  useEffect(() => {
    const handleFarmContextChanged = (event: Event) => {
      const nextFarmContext = (event as CustomEvent<FarmContext>).detail || {};

      setSelectedFarmContext({
        farmId: getStringValue(nextFarmContext.farmId) || undefined,
        farmName: getStringValue(nextFarmContext.farmName) || undefined,
        orgNumber: getStringValue(nextFarmContext.orgNumber) || undefined,
      });
    };

    window.addEventListener(FARM_CONTEXT_CHANGED_EVENT, handleFarmContextChanged);

    return () => {
      window.removeEventListener(FARM_CONTEXT_CHANGED_EVENT, handleFarmContextChanged);
    };
  }, []);

  useEffect(() => {
    if (!cropsCompanies.length) {
      return;
    }

    const storedAccountId = getCookie(cookies.CropsSelectedAccount);
    const storedRoleId = Number(getCookie(cookies.CropsRoleId));

    setSelectedCropsCompanyId(() => {
      const companyForActiveLspFarm = cropsCompanies.find(
        (company) => String(company.extCompanyId) === String(activeFarmId),
      );
      if (companyForActiveLspFarm) {
        return companyForActiveLspFarm.id;
      }

      const matchedFromCookie = cropsCompanies.find((company) => String(company.id) === storedAccountId);
      if (matchedFromCookie) {
        return matchedFromCookie.id;
      }

      return cropsCompanies[0].id;
    });

    if (storedAccountId && storedRoleId > 0) {
      const accountId = Number(storedAccountId);
      setCropsRoleByAccountId((currentValue) => (
        currentValue[accountId]
          ? currentValue
          : { ...currentValue, [accountId]: storedRoleId }
      ));
    }
  }, [activeFarmId, cropsCompanies]);

  const loggedInUsername = getLoggedInUsername()?.toLowerCase();
  const accountInfoEmail = accountInfo?.email?.toLowerCase();
  const accountInfoMatchesSession = !loggedInUsername || !accountInfoEmail || loggedInUsername === accountInfoEmail;
  const onboardingFarmOption = findFarmOptionById(livestockFarmOptions, farmIdForOnboarding);

  useEffect(() => {
    if (
      !onboardingFarmOption
      || requiresFarmOnboarding
      || isLoadingAccountInfo
      || !accountInfoMatchesSession
    ) {
      setAssignedFarmUsers(null);
      return;
    }

    loadAssignedFarmUsers({ id: onboardingFarmOption.farmId }, {
      onSuccess: (data: GetAssignedFarmUsersData) => {
        setAssignedFarmUsers(extractAssignedFarmUsers(data));
      },
      onError: () => {
        setAssignedFarmUsers([]);
      },
    });
  }, [
    accountInfoMatchesSession,
    farmIdForOnboarding,
    isLoadingAccountInfo,
    loadAssignedFarmUsers,
    onboardingFarmOption?.farmId,
    requiresFarmOnboarding,
  ]);

  useEffect(() => {
    if (!farmIdForOnboarding || requiresFarmOnboarding) {
      setIsFarmOnboardingHidden(false);
      return;
    }

    const storageKey = getFarmOnboardingHiddenStorageKey(farmIdForOnboarding);
    setIsFarmOnboardingHidden(localStorage.getItem(storageKey) === '1');
  }, [farmIdForOnboarding, requiresFarmOnboarding]);

  useEffect(() => {
    return () => {
      if (celebrationTimerRef.current !== null) {
        window.clearTimeout(celebrationTimerRef.current);
        celebrationTimerRef.current = null;
      }
      isCelebrationRunningRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!shouldRenderFarmOnboarding) {
      if (celebrationTimerRef.current !== null) {
        window.clearTimeout(celebrationTimerRef.current);
        celebrationTimerRef.current = null;
      }
      isCelebrationRunningRef.current = false;
      setPlayFarmOnboardingCelebration(false);
      setHoldCreateFarmCompletion(false);
      return;
    }

    if (isConfettiDebugEnabled) {
      if (isCelebrationRunningRef.current) {
        return;
      }

      isCelebrationRunningRef.current = true;
      setHoldCreateFarmCompletion(true);
      setPlayFarmOnboardingCelebration(true);

      if (celebrationTimerRef.current !== null) {
        window.clearTimeout(celebrationTimerRef.current);
      }
      celebrationTimerRef.current = window.setTimeout(() => {
        isCelebrationRunningRef.current = false;
        celebrationTimerRef.current = null;
        setPlayFarmOnboardingCelebration(false);
        setHoldCreateFarmCompletion(false);
        if (isCreateFarmCompleted) {
          setCreateFarmCompletionAnimationToken((currentValue) => currentValue + 1);
        }
      }, CONFETTI_PLAY_DURATION_MS);
      return;
    }

    if (!isCreateFarmCompleted) {
      if (celebrationTimerRef.current !== null) {
        window.clearTimeout(celebrationTimerRef.current);
        celebrationTimerRef.current = null;
      }
      isCelebrationRunningRef.current = false;
      setPlayFarmOnboardingCelebration(false);
      setHoldCreateFarmCompletion(false);
      return;
    }

    const isCelebrationPending = sessionStorage.getItem(storageKeys.FarmOnboardingCelebrationPending) === 'true';
    const celebrationFarmId = getStringValue(sessionStorage.getItem(storageKeys.FarmOnboardingCelebrationFarmId));
    const currentFarmId = getStringValue(farmIdForOnboarding);
    const matchesFarm = !celebrationFarmId || !currentFarmId || celebrationFarmId === currentFarmId;

    if (!isCelebrationPending || !matchesFarm) {
      if (celebrationTimerRef.current !== null) {
        window.clearTimeout(celebrationTimerRef.current);
        celebrationTimerRef.current = null;
      }
      isCelebrationRunningRef.current = false;
      setPlayFarmOnboardingCelebration(false);
      setHoldCreateFarmCompletion(false);
      return;
    }

    if (isCelebrationRunningRef.current) {
      return;
    }

    isCelebrationRunningRef.current = true;
    setHoldCreateFarmCompletion(true);
    setPlayFarmOnboardingCelebration(true);
    sessionStorage.removeItem(storageKeys.FarmOnboardingCelebrationPending);
    sessionStorage.removeItem(storageKeys.FarmOnboardingCelebrationFarmId);

    if (celebrationTimerRef.current !== null) {
      window.clearTimeout(celebrationTimerRef.current);
    }
    celebrationTimerRef.current = window.setTimeout(() => {
      isCelebrationRunningRef.current = false;
      celebrationTimerRef.current = null;
      setPlayFarmOnboardingCelebration(false);
      setHoldCreateFarmCompletion(false);
      setCreateFarmCompletionAnimationToken((currentValue) => currentValue + 1);
    }, CONFETTI_PLAY_DURATION_MS);
    return;
  }, [farmIdForOnboarding, isConfettiDebugEnabled, isCreateFarmCompleted, shouldRenderFarmOnboarding]);

  const hideFarmOnboarding = () => {
    if (!farmIdForOnboarding) {
      return;
    }

    localStorage.setItem(getFarmOnboardingHiddenStorageKey(farmIdForOnboarding), '1');
    setIsFarmOnboardingHidden(true);
  };

  const moduleRows = accountInfo?.allowedModules || [];

  const rowsForApp = (appName: SupportedAppName) => {
    const names = appName === 'crops'
      ? ['vaxt']
      : appName === 'bulletinBoard'
        ? ['tavla']
        : ['diko', 'slut'];
    return moduleRows.filter((moduleRow) => names.includes(String(moduleRow?.name || '').toLowerCase()));
  };

  const hasActiveModuleAccess = (appName: SupportedAppName) =>
    rowsForApp(appName).some((moduleRow) => moduleRow?.isActive !== false);

  const hasAppAccess = (appName: string) => {
    if (!isSupportedAppName(appName)) {
      return true;
    }

    if (typeof accessOverrides[appName] === 'boolean') {
      return accessOverrides[appName] as boolean;
    }

    // Access comes from allowedModules (diko/slut, vaxt, tavla) — not has*Account flags.
    return hasActiveModuleAccess(appName);
  };

  const hasActiveCropsModule = hasActiveModuleAccess('crops');

  const hasActiveKemiModule = accountModuleRows.some((moduleRow) => {
    const name = String(moduleRow?.name || '').toLowerCase();
    return name === 'kemi' && moduleRow?.isActive !== false;
  });

  // Full växtodling (vaxt): one card includes crop + spray journal; hide standalone kemi card.
  const hasFullCrops = hasActiveCropsModule;
  const showChemicalJournalCard = !hasActiveCropsModule && (hasFreeChemicalJournalAccess || hasActiveKemiModule);

  const formatTrialRemaining = (value?: string) => {
    const days = trialRemainingDays(value);
    if (days === null) {
      return '';
    }
    if (days === 0) {
      return t('TrialLastDay');
    }
    if (days === 1) {
      return t('TrialRemainingDay');
    }
    return t('TrialRemainingDays', { count: days });
  };

  const isTrialModule = (moduleRow: LspModuleData) => {
    const plan = String(moduleRow?.plan || '').toLowerCase();
    return plan === 'trial' || Boolean(moduleRow?.endsAt) || Boolean(moduleRow?.startsAt);
  };

  const trialPresentation = (appName: SupportedAppName, hasAccess: boolean) => {
    const rows = rowsForApp(appName);
    const activeTrial = rows.find((moduleRow) =>
      isTrialModule(moduleRow) && moduleRow?.isActive !== false && moduleRow?.endsAt);
    const endedTrial = rows.find((moduleRow) =>
      isTrialModule(moduleRow) && moduleRow?.isActive === false);
    if (activeTrial) {
      return {
        disabled: false,
        buttonLabel: t('Open'),
        notice: formatTrialRemaining(activeTrial.endsAt),
        trialAction: undefined as 'start' | undefined,
      };
    }
    if (endedTrial) {
      return {
        disabled: true,
        buttonLabel: t('Open'),
        notice: t('TrialEnded'),
        trialAction: undefined as 'start' | undefined,
      };
    }
    if (!hasAccess) {
      return {
        disabled: false,
        buttonLabel: t('TryNow'),
        notice: undefined as string | undefined,
        trialAction: 'start' as const,
      };
    }
    return {
      disabled: false,
      buttonLabel: t('Open'),
      notice: undefined as string | undefined,
      trialAction: undefined as 'start' | undefined,
    };
  };

  const [startingTrialApp, setStartingTrialApp] = useState<SupportedAppName | null>(null);

  const formatModuleBillingLine = (companyName?: string, companyRegistration?: string) => {
    const parts = [companyName, companyRegistration].filter(Boolean);
    return parts.length ? parts.join(' · ') : undefined;
  };

  const billingLinesForModuleKeys = (moduleKeys: string[]) => {
    const seen = new Set<string>();
    const lines: string[] = [];

    accountModuleRows.forEach((moduleRow) => {
      const moduleKey = String(moduleRow?.name || '').toLowerCase();
      if (!moduleKeys.includes(moduleKey)) {
        return;
      }
      const line = formatModuleBillingLine(
        moduleRow?.billingCompanyName,
        moduleRow?.billingCompanyRegistration,
      );
      if (!line || seen.has(line)) {
        return;
      }
      seen.add(line);
      lines.push(line);
    });

    return lines;
  };

  const billingLinesForApp = (appName: SupportedAppName) => {
    const moduleKeys =
      appName === 'livestock' ? ['diko', 'slut'] : appName === 'crops' ? ['vaxt'] : ['tavla'];
    return billingLinesForModuleKeys(moduleKeys);
  };

  const beginTrial = async (appName: SupportedAppName, billingCompanyId: number) => {
    setStartingTrialApp(appName);
    try {
      await selfStartTrial(appName, activeFarmId, billingCompanyId);
      if ((appName === 'crops' || appName === 'bulletinBoard') && activeFarmId) {
        try {
          await selfSyncFarmToCrops(activeFarmId);
          await refetchCropsCompanies();
        } catch {
          // Trial modules are granted even if Crops link fails; user can retry from app selection sync.
        }
      }
      await refetchAccountInfo();
    } catch (error) {
      const details = JSON.stringify((error as { response?: { data?: unknown } })?.response?.data || '');
      if (details.includes('billing.company.required') || details.includes('billing.company.invalid')) {
        message.error(t('BillingCompanyRequiredBeforeTrial'));
      } else {
        message.error(details.includes('trial.already.used') ? t('TrialAlreadyUsed') : t('AnErrorOccurred'));
      }
    } finally {
      setStartingTrialApp(null);
    }
  };

  const resolveTrialBillingCompanies = async () => {
    // Prefer companies for this farm's organisation (same rule as the trial API).
    const orgCompanies = getBillingCompaniesForFarm(organisations, activeFarmId).map((company) => ({
      id: company.id,
      name: company.name,
      companyRegistration: company.companyRegistration,
      active: company.active,
    }));
    if (orgCompanies.length > 0) {
      return orgCompanies;
    }

    if (isLoadingBillingCompanies || isFetchingBillingCompanies || isLoadingOrganisations) {
      const refreshed = await refetchBillingCompanies();
      const fromApi = (refreshed.data ?? billingCompanies).filter((company) => company.active !== false);
      if (fromApi.length > 0) {
        return fromApi;
      }
    }

    const fromApi = billingCompanies.filter((company) => company.active !== false);
    if (fromApi.length > 0) {
      return fromApi;
    }

    const retried = await refetchBillingCompanies();
    return (retried.data ?? []).filter((company) => company.active !== false);
  };

  const promptTrialBillingCompany = async (appName: SupportedAppName) => {
    const companies = await resolveTrialBillingCompanies();
    if (companies.length === 0) {
      Modal.warning({
        title: t('BillingCompanyRequiredBeforeTrial'),
        okText: t('Continue'),
        onOk: () => navigate('/companies'),
      });
      return;
    }
    // Always confirm the billing company, even when there is only one.
    trialBillingPickRef.current = companies[0].id;
    Modal.confirm({
      title: t('StartTrial'),
      content: (
        <Select
          style={{ width: '100%', marginTop: 12 }}
          defaultValue={companies[0].id}
          onChange={(value) => {
            trialBillingPickRef.current = Number(value);
          }}
          options={companies.map((company) => ({
            value: company.id,
            label: `${company.name ?? ''} ${company.companyRegistration ?? ''}`.trim() || String(company.id),
          }))}
        />
      ),
      okText: t('StartTrial'),
      cancelText: t('Cancel'),
      onOk: () => beginTrial(appName, trialBillingPickRef.current ?? companies[0].id),
    });
  };

  const isAppAccessLoading = (appName: string) => {
    const isCurrentAppModuleRequest =
      enablingAppName === appName && (isLoadingAllModules || isSelfAddingModuleToAccount);

    if (appName === 'livestock') {
      return isSelfEnablingLspAccess || isCurrentAppModuleRequest;
    }

    if (appName === 'crops') {
      return isSelfEnablingCropsAccess || isCurrentAppModuleRequest;
    }

    if (appName === 'bulletinBoard') {
      return isSelfEnablingBoardAccess || isCurrentAppModuleRequest;
    }

    return false;
  };

  const enableAppAccess = async (appName: SupportedAppName) => {
    if (appName === 'livestock') {
      await selfEnableLspAccess();
      return;
    }

    if (appName === 'crops') {
      await selfEnableCropsAccess();
      return;
    }

    await selfEnableBoardAccess();
  };

  const getModuleIdForApp = async (appName: SupportedAppName) => {
    const cachedModuleId = moduleIdOverrides[appName];

    if (cachedModuleId !== undefined && cachedModuleId !== null) {
      return cachedModuleId;
    }

    const modules = await getAllModules();
    const moduleId = findModuleIdForApp(modules || [], appName);

    if (moduleId === undefined || moduleId === null) {
      message.error(t('AnErrorOccurred'));
      throw new Error(`Missing module id for "${appName}"`);
    }

    setModuleIdOverrides((currentValue) => ({
      ...currentValue,
      [appName]: moduleId,
    }));

    return moduleId;
  };

  const addModuleAccessForApp = async (appName: SupportedAppName) => {
    const moduleId = await getModuleIdForApp(appName);
    await selfAddModuleToAccount(moduleId);
  };

  const activateAppAccess = async (appName: SupportedAppName) => {
    if (enablingAppName) {
      return false;
    }

    setEnablingAppName(appName);

    try {
      await enableAppAccess(appName);
      await addModuleAccessForApp(appName);
      setAccessOverrides((currentValue) => ({
        ...currentValue,
        [appName]: true,
      }));
      void refetchAccountInfo();
      return true;
    } catch {
      return false;
    } finally {
      setEnablingAppName(null);
    }
  };

  const persistCropsSelectionCookies = (accountId: number, roleId?: number) => {
    if (roleId && roleId > 0) {
      setCropsRoleByAccountId((currentValue) => (
        currentValue[accountId] === roleId
          ? currentValue
          : { ...currentValue, [accountId]: roleId }
      ));
    }

    setCropsSelectionCookies(accountId, roleId);
  };

  const handleHeaderFarmChange = useCallback(async (nextFarmValue: string | number) => {
    const nextFarmOption = findFarmOptionById(livestockFarmOptions, nextFarmValue);

    if (!nextFarmOption || !selectedLivestockFarm || nextFarmOption.farmId === selectedLivestockFarm.farmId) {
      return;
    }

    const previousFarmContext = {
      farmId: selectedLivestockFarm.farmId,
      farmName: selectedLivestockFarm.farmName,
      orgNumber: selectedLivestockFarm.orgNumber,
    };

    setCurrentFarmContext({
      farmId: nextFarmOption.farmId,
      farmName: nextFarmOption.farmName,
      orgNumber: nextFarmOption.orgNumber,
    });

    const linkedCropsCompany = cropsCompanies.find((company) => String(company.extCompanyId) === nextFarmOption.farmId);
    if (linkedCropsCompany) {
      setSelectedCropsCompanyId(linkedCropsCompany.id);
      persistCropsSelectionCookies(
        linkedCropsCompany.id,
        getCropsRoleId(linkedCropsCompany, cropsRoleByAccountId),
      );
    }

    try {
      await setAccountFarm(nextFarmOption.farmId);
      void refetchAccountInfo();
    } catch {
      setCurrentFarmContext(previousFarmContext);
      message.error(t('AnErrorOccurred'));
    }
  }, [
    cropsCompanies,
    cropsRoleByAccountId,
    livestockFarmOptions,
    refetchAccountInfo,
    selectedLivestockFarm,
    setAccountFarm,
    t,
  ]);

  useEffect(() => {
    if (!livestockPickerOptions.length && !isLoadingAccountInfo) {
      setFarmPicker(null);
      return () => setFarmPicker(null);
    }

    const activeFarmIdForContext = selectedLivestockFarm?.farmId || activeFarmId;
    const { hierarchy, farmRoleName } = getFarmPickerContextMeta(
      organisations,
      accountInfo,
      activeFarmIdForContext,
    );

    const pickerOptions =
      selectedLivestockFarm &&
      !livestockPickerOptions.some((option) => String(option.value) === String(selectedLivestockFarm.farmId))
        ? [
            ...livestockPickerOptions,
            {
              value: selectedLivestockFarm.farmId,
              label: selectedLivestockFarm.label,
            },
          ]
        : livestockPickerOptions;

    setFarmPicker({
      disabled: appsLockedUntilFarm || isSettingAccountFarm,
      farmRoleName,
      hierarchy,
      label: t('ActiveFarm'),
      loading: isLoadingAccountInfo || isSettingAccountFarm || linkingCropsFarm || isLoadingOrganisations,
      onChange: (nextFarmValue) => {
        void handleHeaderFarmChange(nextFarmValue);
      },
      options: pickerOptions,
      placeholder: t('CropsFarmPickerPlaceholder'),
      value: selectedLivestockFarm?.farmId,
    });

    return () => setFarmPicker(null);
  }, [
    accountInfo,
    activeFarmId,
    appsLockedUntilFarm,
    handleHeaderFarmChange,
    isLoadingAccountInfo,
    isLoadingOrganisations,
    isSettingAccountFarm,
    linkingCropsFarm,
    livestockPickerOptions,
    organisations,
    selectedLivestockFarm?.farmId,
    selectedLivestockFarm?.value,
    setFarmPicker,
    t,
  ]);

  const redirectToApp = (item: AppCardItem) => {
    const baseUrl = item.url;
    if (!baseUrl) return;

    const storedAuth = localStorage.getItem(cookies.FarmersPlanToken);

    if (!storedAuth) {
      return;
    }

    const { username, token } = JSON.parse(storedAuth);

    const params = new URLSearchParams({
      token: token,
      username: username,
    });
  
    if (completeRegistration) {
      params.append('completeRegistration', 'true');
    }

    setCookie('token', token, CROPS_COOKIE_DAYS, CROPS_COOKIE_DOMAIN);
    const usesCropsCompany = item.name === 'crops' || item.name === 'bulletinBoard' || item.name === 'chemicalJournal';
    const cropsCompany = selectedCropsCompany || cropsCompanyForActiveFarm;
    if (usesCropsCompany && cropsCompany) {
      persistCropsSelectionCookies(
        cropsCompany.id,
        getCropsRoleId(cropsCompany, cropsRoleByAccountId),
      );
    } else if (!usesCropsCompany && activeFarmId) {
      setCookie(cookies.FarmIdCookie, activeFarmId, CROPS_COOKIE_DAYS, CROPS_COOKIE_DOMAIN);
    }
    // const hashIndex = baseUrl?.indexOf('#');
    let finalUrl: string;

    // if (hashIndex !== -1) {
    //   const beforeHash = baseUrl?.substring(0, hashIndex);
    //   const afterHash = baseUrl?.substring(hashIndex);
    //   finalUrl = `${beforeHash}${afterHash}?${params.toString()}`;
    // } else {
      finalUrl = `${baseUrl}?${params.toString()}`;
    // }

    window.location.href = finalUrl;
  };

  const handleGo = async (item: AppCardItem) => {
    if (item.disabled) {
      return;
    }

    if (appsLockedUntilFarm) {
      message.warning(t('CreateFarmBeforeOpeningApp'));
      return;
    }

    if (item.trialAction === 'start' && isSupportedAppName(item.name)) {
      const appName = item.name;
      Modal.confirm({
        title: t('TryNow'),
        content: t('TrialExplain'),
        okText: t('StartTrial'),
        cancelText: t('Cancel'),
        onOk: () => promptTrialBillingCompany(appName),
      });
        return;
    }


    if (enablingAppName) {
      return;
    }

    // Do not auto-enable apps here: Admin toggles (Livestock / Crops / Bulletin board)
    // must stay in control. Cards without access are already disabled via hasAppAccess.

    redirectToApp(item);
  };

  const closeWelcomeModal = () => {
    setShowWelcomeModal(false);
    navigate(location.pathname, { replace: true, state: { ...location.state, showWelcome: false } });
  };

  const bulletinBoardUrl = getBulletinBoardUrl();
  const renderCardBillingCompany = (billingLines: string[]) => {
    const loading = isLoadingAccountInfo || isSettingAccountFarm;

    if (loading) {
      return (
        <Typography.Text style={{ display: 'block', fontSize: 12, color: '#98a2b3', textAlign: 'left' }}>
          {t('ContextCompany')}…
        </Typography.Text>
      );
    }

    if (!billingLines.length) {
      return null;
    }

    return (
      <div style={{ textAlign: 'left', width: '100%', height: '100%' }}>
        <Typography.Text
          style={{
            display: 'block',
            marginBottom: 4,
            fontSize: 12,
            fontWeight: 600,
            color: '#667085',
            letterSpacing: '0.02em',
          }}
        >
          {t('ContextCompany')}
        </Typography.Text>
        <Typography.Text
          ellipsis
          style={{
            display: 'block',
            fontSize: 14,
            fontWeight: 600,
            color: '#344054',
            lineHeight: 1.35,
          }}
        >
          {billingLines[0]}
        </Typography.Text>
      </div>
    );
  };
  const applications = [
    {
      name: 'livestock',
      imageSrc: lsp,
      title: t('Livestock'),
      description: t('LivestockCardDescription'),
      features: [
        { text: t('LivestockFeature1'), effect: t('LivestockFeature1Effect') },
        { text: t('LivestockFeature2'), effect: t('LivestockFeature2Effect') },
        { text: t('LivestockFeature3'), effect: t('LivestockFeature3Effect') },
        { text: t('LivestockFeature4'), effect: t('LivestockFeature4Effect') },
        { text: t('LivestockFeature5'), effect: t('LivestockFeature5Effect') },
        { text: t('PhoneSupport') },
      ],
      awaitingFarm: appsLockedUntilFarm,
      disabled: appsLockedUntilFarm || trialPresentation('livestock', hasAppAccess('livestock')).disabled,
      buttonLabel: trialPresentation('livestock', hasAppAccess('livestock')).buttonLabel,
      notice: trialPresentation('livestock', hasAppAccess('livestock')).notice,
      trialAction: trialPresentation('livestock', hasAppAccess('livestock')).trialAction,
      isLoading: enablingAppName === 'livestock' && isAppAccessLoading('livestock') || startingTrialApp === 'livestock',
      url: process.env.REACT_APP_LOGIN_PAGE_URL,
      aboveCta: renderCardBillingCompany(billingLinesForApp('livestock')),
    },
    {
      name: 'crops',
      imageSrc: crops,
      title: hasFullCrops ? t('CropsAndChemicalJournal') : t('Crops'),
      description: hasFullCrops ? t('CropsAndChemicalJournalCardDescription') : t('CropsCardDescription'),
      features: [
        { text: t('CropsFeature1'), effect: t('CropsFeature1Effect') },
        { text: t('CropsFeature2'), effect: t('CropsFeature2Effect') },
        { text: t('CropsFeature3'), effect: t('CropsFeature3Effect') },
        { text: t('CropsFeature4'), effect: t('CropsFeature4Effect') },
        { text: t('CropsFeature5'), effect: t('CropsFeature5Effect') },
        { text: t('CropsFeature6'), effect: t('CropsFeature6Effect') },
        { text: t('PhoneSupport') },
      ],
      awaitingFarm: cropsAppsLocked,
      disabled: cropsTrialBlocked || trialPresentation('crops', hasAppAccess('crops')).disabled,
      buttonLabel: trialPresentation('crops', hasAppAccess('crops')).buttonLabel,
      notice: trialPresentation('crops', hasAppAccess('crops')).notice,
      trialAction: trialPresentation('crops', hasAppAccess('crops')).trialAction,
      isLoading: enablingAppName === 'crops' && isAppAccessLoading('crops') || startingTrialApp === 'crops',
      url: process.env.REACT_APP_CROPS_PAGE_URL,
      aboveCta: renderCardBillingCompany(billingLinesForApp('crops')),
    },
    {
      name: 'chemicalJournal',
      imageSrc: chemicalJournalImage,
      title: t('ChemicalJournal'),
      description: t('ChemicalJournalCardDescription'),
      features: [
        { text: t('ChemicalJournalFeature1'), effect: t('ChemicalJournalFeature1Effect') },
        { text: t('ChemicalJournalFeature2'), effect: t('ChemicalJournalFeature2Effect') },
        { text: t('ChemicalJournalFeature3'), effect: t('ChemicalJournalFeature3Effect') },
        { text: t('PhoneSupport'), excluded: true },
      ],
      awaitingFarm: chemicalJournalLocked,
      disabled: chemicalJournalLocked,
      buttonLabel: t('Open'),
      isLoading: linkingCropsFarm && !hasFreeChemicalJournalAccess,
      url: getChemicalJournalUrl(),
      aboveCta: renderCardBillingCompany(billingLinesForModuleKeys(['kemi'])),
    },
    {
      name: 'bulletinBoard',
      imageSrc: bulletinBoard,
      title: t('BulletinBoard'),
      description: t('BulletinBoardCardDescription'),
      features: [
        { text: t('BulletinBoardFeature1'), effect: t('BulletinBoardFeature1Effect') },
        { text: t('BulletinBoardFeature2'), effect: t('BulletinBoardFeature2Effect') },
        { text: t('BulletinBoardFeature3'), effect: t('BulletinBoardFeature3Effect') },
        { text: t('BulletinBoardFeature4'), effect: t('BulletinBoardFeature4Effect') },
        { text: t('BulletinBoardFeature5'), effect: t('BulletinBoardFeature5Effect') },
        { text: t('PhoneSupport') },
      ],
      awaitingFarm: cropsAppsLocked,
      disabled: cropsTrialBlocked || trialPresentation('bulletinBoard', hasAppAccess('bulletinBoard')).disabled,
      buttonLabel: trialPresentation('bulletinBoard', hasAppAccess('bulletinBoard')).buttonLabel,
      notice: trialPresentation('bulletinBoard', hasAppAccess('bulletinBoard')).notice,
      trialAction: trialPresentation('bulletinBoard', hasAppAccess('bulletinBoard')).trialAction,
      isLoading: enablingAppName === 'bulletinBoard' && isAppAccessLoading('bulletinBoard') || startingTrialApp === 'bulletinBoard',
      url: bulletinBoardUrl,
      aboveCta: renderCardBillingCompany(billingLinesForApp('bulletinBoard')),
    },
    // {
    //   name: 'medicine',
    //   title: t('medicineJournal'),
    //   disabled: false,
    //   url: process.env.REACT_APP_MEDICINE_PAGE_URL,
    // },
    // {
    //   title: t('cropsCalendar'),
    //   description: t('cropsCalendarDescription'),
    //   disabled: false,
    //   url: process.env.REACT_APP_CROPS_PAGE_URL,
    // },
    // {
    //   name: 'breeding',
    //   title: t('breedingActivities'),
    //   disabled: false,
    //   url: process.env.REACT_APP_BREEDING_PAGE_URL,
    // },
  ];
  const visibleApplications = applications.filter((app) => app.name !== 'chemicalJournal' || showChemicalJournalCard);
  const reserveCompanySlot = visibleApplications.some((app) => Boolean(app.aboveCta));
  const wideCardSpan = visibleApplications.length === 3 ? 8 : 6;

  return (
    <Spin spinning={isLoadingCropsAccounts || isLoadingAccountInfo}>
      <div style={{ 
        minHeight: '100vh',
        background: 'transparent',
        padding: isMobile ? `12px ${pageSidePadding}px 64px` : `24px ${pageSidePadding}px 88px`,
        display: 'flex',
        width: '100%',
        boxSizing: 'border-box',
        marginTop: 0,
      }}>
        <div style={{ 
          width: '100%',
          margin: '0 auto',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}>
        {/* <Title 
          level={1} 
          style={{ 
            textAlign: 'center', 
            marginBottom: isMobile ? 28 : 48,
            fontSize: isMobile ? 34 : 50,
            fontWeight: 600
          }}
        >
          {t('chooseApplication')}
        </Title> */}
        <Modal 
          open={showNoCropsModal}
          onOk={() => setShowNoCropsModal(false)}
          onCancel={() => setShowNoCropsModal(false)}
        >
          <Typography.Title level={2}>{t('CropsAccessTitle')}</Typography.Title>
          <Typography.Text>{t('noCropsAccountMessage')}</Typography.Text>
        </Modal>
        <Modal
          open={showWelcomeModal}
          closable={false}
          maskClosable={false}
          footer={null}
        >
          <Typography.Title level={2} style={{ marginTop: 0 }}>{t('WelcomeModalTitle')}</Typography.Title>
          <Typography.Paragraph style={{ fontSize: 15 }}>{t('WelcomeModalCreateFarmText')}</Typography.Paragraph>
          <Typography.Paragraph style={{ fontSize: 15 }}>{t('WelcomeModalTrialText')}</Typography.Paragraph>
          <Button type="primary" block size="large" onClick={closeWelcomeModal}>
            {t('WelcomeModalButton')}
                  </Button>
        </Modal>
        {shouldRenderFarmOnboarding ? (
          <div
            style={{
              width: cardsContentWidth,
              maxWidth: cardsContentMaxWidth,
              marginBottom: isMobile ? 16 : 20,
              position: 'relative',
            }}
          >
            <FarmOnboarding
              createFarmCompletionAnimationToken={createFarmCompletionAnimationToken}
              holdCreateFarmCompletion={holdCreateFarmCompletion}
              isCreateFarmCompleted={isCreateFarmCompleted}
              isInviteUsersCompleted={isInviteUsersCompleted}
              isMobile={isMobile}
              onHideFarmOnboarding={hideFarmOnboarding}
              onCreateFarm={() => {
                if ((organisations ?? []).length > 0) {
                  navigate('/companies');
                  return;
                }
                if (activeFarmId) {
                  navigate(`/farm-details?id=${encodeURIComponent(activeFarmId)}`);
                  return;
                }
                navigate('/companies/setup');
              }}
              onInviteUsers={() => navigate('/users', { state: { openInviteModal: true } })}
              shouldShowHideFarmOnboardingButton={shouldShowHideFarmOnboardingButton}
              showInviteUsersStep={canInviteFarmUsers(accountInfo)}
            />
            <ConfettiBurst isActive={playFarmOnboardingCelebration} />
          </div>
        ) : null}
        <div
          style={{
            width: cardsGridWidth,
            maxWidth: cardsGridMaxWidth,
          }}
        >
          <Row
            gutter={[horizontalCardGutter, cardSpacing]}
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'stretch',
              width: isMobile ? '100%' : `calc(100% + ${horizontalCardGutter*2}px)`,
              flex: '1 1 100%',
              marginLeft: isMobile ? 0 : -cardsGridOffset*2,
              marginRight: isMobile ? 0 : -cardsGridOffset*2,
            }}
          >
            {visibleApplications.map((app, index) => (
              <Col xs={24} md={12} xl={wideCardSpan} key={index} style={{ display: 'flex', alignSelf: 'stretch' }}>
                <div style={{ width: '100%', display: 'flex', height: '100%' }}>
                  <AppCard
                    item={app}
                    onGo={handleGo}
                    reserveCompanySlot={reserveCompanySlot}
                    // isRemembered={rememberedApp === app.title}
                    // onRememberChange={handleRememberChange}
                  />
                </div>
              </Col>
            ))}
          </Row>
        </div>
        <a
          href="https://farmersplan.com/priser"
          target="_blank"
          rel="noopener noreferrer"
          style={{ marginTop: isMobile ? 20 : 28, fontSize: 15, fontWeight: 600, textDecoration: 'underline' }}
        >
          {t('SeePricing')}
        </a>
        </div>
      </div>
    </Spin>

  );
};

export default ChooseApplicationPage;
